import { usePathname } from 'next/navigation';
import { useContext, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { CalmGradients, FocusRingVariants } from '@calm-web/design-system';

import { onboardingTiles } from '@/components/homepage/OnboardingQuestionnaire/data';
import { Tile } from '@/components/ui/QuestionnaireTile/types';
import { useAnalytics } from '@/hooks/analytics';
import { useMountToggle } from '@/hooks/utils';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import genericMessages from '@/messages/messages';
import { postQuestionnairePoll } from '@/utils/endpoints';

import AnimatedScreenWrapper from '../../Components/AnimatedScreenWrapper';
import { PreSignupFlowContext } from '../../PreSignupProvider';
import { SelectedTiles } from '../../PreSignupProvider/types';
import messages from './messages';
import { Answer, Body, Button, GoalsListWrapper, Heading, SkipButton, Wrapper } from './styles';

const OnboardingGoals = () => {
	const { skipToEnd, nextScreen, isEmbedded } = useContext(PreSignupFlowContext);
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const isEntering = useMountToggle();
	const { isEnrolled } = useAmplitudeExperiment('www-new-ftue-questionnaire');
	const routerPush = useRouterPush();
	const pathname = usePathname();

	const [selectedTiles, setSelectedTiles] = useState<SelectedTiles>({});

	function isSelected(tileId: string): boolean {
		return !!selectedTiles[tileId];
	}

	function onTileClick(tile: Tile): void {
		const { id } = tile;
		const newSelectedTiles = { ...selectedTiles, [id]: tile };

		if (isSelected(id)) {
			delete newSelectedTiles[id];
		}

		setSelectedTiles(newSelectedTiles);
	}

	/**
	 * Animate out and change screens
	 */
	async function onContinue(): Promise<void> {
		const selectedTilesArr = Object.keys(selectedTiles)
			.map(i => selectedTiles[i])
			.filter(i => i);

		const answers = selectedTilesArr.map(({ id }) => id);

		await postQuestionnairePoll([
			{
				poll_name: 'goals_web_stress_onboarding',
				question: 'What can we help with today?',
				answers,
			},
		]);

		logEvent({
			eventName: 'FTUE : Goal Questionnaire : Proceeded',
			eventProps: {
				choices: answers,
				num_choices: selectedTilesArr?.length,
			},
		});

		const isSleepFlow = answers.includes('sleep_quality') && answers.length === 1;
		const questionnaireBFocus = isSleepFlow ? 'sleep_questionnaireB' : 'stress_questionnaireB';

		// Embedded mode (post-payment) and the www-new-ftue-questionnaire experiment both
		// route into the 3-poll questionnaireB flow via routerPush.
		if ((isEmbedded || isEnrolled) && pathname) {
			return routerPush(pathname, { focus: questionnaireBFocus });
		}

		nextScreen({
			flowKey: isSleepFlow ? 'sleep' : 'stress',
			screenKey: 'start',
		});
	}

	/**
	 * Animate out and change screens
	 */
	async function onGoalsSkip(): Promise<void> {
		logEvent({ eventName: 'FTUE : Goal Questionnaire : Skipped' });
		skipToEnd();
	}

	const titleId = 'onboarding-goals-title';
	const subtitleId = 'onboarding-goals-subtitle';
	return (
		<AnimatedScreenWrapper>
			<Wrapper>
				<Heading id={titleId}>{formatMessage(messages.title)}</Heading>
				<Body id={subtitleId}>{formatMessage(messages.cta)}</Body>
				<GoalsListWrapper aria-labelledby={titleId} aria-describedby={subtitleId}>
					{onboardingTiles.map((tile, index) => (
						<li key={tile.id}>
							<Answer
								title={formatMessage(tile.title as MessageDescriptor)}
								colors={{
									color1: CalmGradients[index][0],
									color2: CalmGradients[index][1],
								}}
								onClick={(): void => onTileClick(tile)}
								isSelected={isSelected(tile.id)}
								Icon={tile.Icon}
								$isEntering={isEntering}
								$transitionDelay={index * 20}
								focusRingVariant={FocusRingVariants.Dark}
								role="checkbox"
							/>
						</li>
					))}
				</GoalsListWrapper>
				<Button
					fullWidth
					backgroundColor="accessibleBrandGradient"
					textColor="white"
					onPress={onContinue}
					focusRingVariant={FocusRingVariants.Dark}
				>
					{formatMessage(genericMessages.continue)}
				</Button>
				<SkipButton
					backgroundColor="gray1"
					textColor="gray8"
					onPress={onGoalsSkip}
					data-testid="onboarding-skip-button"
					focusRingVariant={FocusRingVariants.Dark}
				>
					{formatMessage(genericMessages.skipForNow)}
				</SkipButton>
			</Wrapper>
		</AnimatedScreenWrapper>
	);
};

export default OnboardingGoals;
